import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import axios from "axios";
import { connect, useSelector } from "react-redux";
import {
  clearFamilyClan,
  getFamilyClan,
  getFamilyClanAnalytics,
} from "../store/familyClan/action";
import noImage from "../assets/images/noImage.png";
import "../assets/css/familyClans.css";

const numberFormat = new Intl.NumberFormat();
const monthFormatter = new Intl.DateTimeFormat(undefined, { month: "long" });
const monthOptions = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: monthFormatter.format(new Date(2026, index, 1)),
}));

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date(value))
    : "—";

const DetailRow = ({ label, children }) => (
  <div className="family-clan-detail-row">
    <dt>{label}</dt>
    <dd>{children || "—"}</dd>
  </div>
);

const ResettableDetailRow = ({
  label,
  field,
  children,
  onReset,
  resettingField,
}) => (
  <div className="family-clan-detail-row family-clan-detail-row-resettable">
    <dt>{label}</dt>
    <dd>
      <div className="family-clan-detail-value">
        <div className="family-clan-detail-copy">{children || "—"}</div>
        <button
          type="button"
          className="btn btn-sm family-clan-reset-button"
          onClick={() => onReset(field)}
          disabled={resettingField === field}
        >
          {resettingField === field ? "Resetting..." : "Reset"}
        </button>
      </div>
    </dd>
  </div>
);

const SummaryCard = ({ label, value, icon }) => (
  <article className="family-clan-summary-card">
    <div>
      <p>{label}</p>
      <strong>{numberFormat.format(value || 0)}</strong>
    </div>
  </article>
);

const FamilyClanDetail = ({
  getFamilyClan,
  getFamilyClanAnalytics,
  clearFamilyClan,
}) => {
  const { id } = useParams();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [resettingField, setResettingField] = useState("");
  const [resetFeedback, setResetFeedback] = useState(null);
  const [rosterMembers, setRosterMembers] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterError, setRosterError] = useState("");
  const [showDateRange, setShowDateRange] = useState(false);
  const [contributionStartDate, setContributionStartDate] = useState("");
  const [contributionEndDate, setContributionEndDate] = useState("");
  const [appliedDateRange, setAppliedDateRange] = useState(null);
  const {
    selectedFamilyClan: clan,
    analytics,
    detailLoading,
    detailError,
    analyticsLoading,
    analyticsError,
  } = useSelector((state) => state.familyClan);

  useEffect(() => {
    getFamilyClan(id, "contribution");
    return clearFamilyClan;
  }, [id, getFamilyClan, clearFamilyClan]);

  useEffect(() => {
    getFamilyClanAnalytics(id, selectedMonth, selectedYear);
  }, [id, selectedMonth, selectedYear, getFamilyClanAnalytics]);

  const loadRoster = useCallback(async ({ startDate, endDate } = {}) => {
    setRosterLoading(true);
    setRosterError("");

    try {
      const response = await axios.get(`familyClan/${id}/members`, {
        params: {
          sort: "contribution",
          start: 0,
          limit: 100,
          ...(startDate ? { startDate } : {}),
          ...(endDate ? { endDate } : {}),
        },
      });

      if (!response.data.status) throw new Error(response.data.message);
      setRosterMembers(response.data.data?.members || []);
    } catch (error) {
      setRosterError(
        error.response?.data?.message ||
          error.message ||
          "Unable to load clan members.",
      );
    } finally {
      setRosterLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const handleContributionRangeSubmit = (event) => {
    event.preventDefault();

    if (!contributionStartDate || !contributionEndDate) {
      setRosterError("Choose both a start date and an end date.");
      return;
    }

    if (
      contributionStartDate &&
      contributionEndDate &&
      contributionStartDate > contributionEndDate
    ) {
      setRosterError("The start date cannot be after the end date.");
      return;
    }

    const range = {
      startDate: contributionStartDate,
      endDate: contributionEndDate,
    };
    setAppliedDateRange(range);
    loadRoster(range);
  };

  const handleClearContributionRange = () => {
    setContributionStartDate("");
    setContributionEndDate("");
    setAppliedDateRange(null);
    setRosterError("");
    loadRoster();
  };

  const handleResetField = async (field) => {
    setResettingField(field);
    setResetFeedback(null);

    try {
      const response = await axios.post(`familyClan/${id}/reset-field`, {
        field,
      });

      if (!response.data.status) {
        throw new Error(response.data.message);
      }

      setResetFeedback({
        type: "success",
        message:
          field === "rules"
            ? "Rules cleared successfully."
            : "Clan field reset successfully.",
      });
      await getFamilyClan(id, "contribution");
    } catch (error) {
      setResetFeedback({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          "Unable to reset clan field.",
      });
    } finally {
      setResettingField("");
    }
  };

  if (detailLoading)
    return (
      <div className="family-clans-state family-clans-detail-loading">
        <span
          className="spinner-border spinner-border-sm me-2"
          role="status"
        ></span>
        Loading clan details…
      </div>
    );

  if (detailError)
    return (
      <div className="card family-clans-state">
        <p className="text-danger mb-3">{detailError}</p>
        <Link to="/admin/familyClans" className="btn btn-sm btn-outline-light">
          Back to Family Clans
        </Link>
      </div>
    );

  if (!clan) return null;

  const startYear = new Date(clan.createdAt || now).getFullYear();
  const endYear = now.getFullYear();
  const yearOptions = [];
  for (let year = endYear; year >= startYear; year -= 1) {
    yearOptions.push(year);
  }
  if (!yearOptions.length) yearOptions.push(endYear);

  const summaryCards = [
    {
      label: "Today contribution",
      value: analytics?.totals?.todayContribution,
      icon: "fas fa-sun",
    },
    {
      label: "This week contribution",
      value: analytics?.totals?.weekContribution,
      icon: "fas fa-calendar-week",
    },
    {
      label: "This month contribution",
      value: analytics?.totals?.monthContribution,
      icon: "fas fa-chart-column",
    },
    {
      label: "Total contribution",
      value: analytics?.totals?.totalContribution,
      icon: "fas fa-trophy",
    },
  ];

  const chartData = {
    labels: analytics?.chart?.map((entry) => entry.day) || [],
    datasets: [
      {
        label: analytics?.selectedMonthLabel || "Monthly contributions",
        data: analytics?.chart?.map((entry) => entry.amount) || [],
        backgroundColor: "rgba(25, 118, 210, 0.82)",
        borderRadius: 10,
        borderSkipped: false,
        maxBarThickness: 28,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `Contribution: ${numberFormat.format(context.parsed.y || 0)}`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#a9aab3",
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: "#a9aab3",
          callback: (value) => numberFormat.format(value),
        },
        grid: {
          color: "rgba(255,255,255,.08)",
        },
      },
    },
  };

  return (
    <article className="family-clan-detail" aria-labelledby="family-clan-name">
      <div
        className="family-clan-hero"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(13,13,20,.08), rgba(13,13,20,.96)), url(${clan.coverPicture || noImage})`,
        }}
      >
        <Link to="/admin/familyClans" className="family-clan-back">
          <i className="fas fa-arrow-left" aria-hidden="true"></i> Family Clans
        </Link>
        <button
          type="button"
          className="btn btn-sm family-clan-image-reset family-clan-cover-reset"
          onClick={() => handleResetField("coverPicture")}
          disabled={resettingField === "coverPicture"}
        >
          {resettingField === "coverPicture" ? "Resetting..." : "Reset cover"}
        </button>
        <div className="family-clan-hero-content">
          <div className="family-clan-profile-wrap">
            <img
              className="family-clan-profile"
              src={clan.profilePicture || noImage}
              onError={(event) => {
                event.currentTarget.src = noImage;
              }}
              alt={`${clan.name} profile`}
            />
            <button
              type="button"
              className="btn btn-sm family-clan-image-reset family-clan-profile-reset"
              onClick={() => handleResetField("profilePicture")}
              disabled={resettingField === "profilePicture"}
            >
              {resettingField === "profilePicture"
                ? "Resetting..."
                : "Reset profile"}
            </button>
          </div>
          <div>
            <h1 id="family-clan-name">{clan.name}</h1>
            <p>{clan.catchPhrase || "No catch phrase set."}</p>
          </div>
          <div className="family-clan-hero-stats">
            <span>
              <strong>
                {numberFormat.format(
                  clan.memberCount || clan.members?.length || 0,
                )}
              </strong>{" "}
              members
            </span>
            <span>
              <strong>{numberFormat.format(clan.exp || 0)}</strong> EXP
            </span>
          </div>
        </div>
      </div>

      <section
        className="family-clan-summary-grid"
        aria-label="Contribution summary"
      >
        {summaryCards.map((card) => (
          <SummaryCard
            key={card.label}
            label={card.label}
            value={card.value}
            icon={card.icon}
          />
        ))}
      </section>

      <section
        className="card family-clan-chart-card"
        aria-labelledby="family-clan-chart-title"
      >
        <div className="card-header family-clan-chart-header">
          <div>
            <h2 className="text-white" id="family-clan-chart-title">
              Monthly contributions
            </h2>
            <p className="family-clans-subtitle mb-0">
              Daily contribution totals for{" "}
              {analytics?.selectedMonthLabel || "the selected month"}.
            </p>
          </div>
          <div className="family-clan-chart-filters">
            <label>
              <span>Month</span>
              <select
                className="form-control"
                value={selectedMonth}
                onChange={(event) =>
                  setSelectedMonth(Number(event.target.value))
                }
              >
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Year</span>
              <select
                className="form-control"
                value={selectedYear}
                onChange={(event) =>
                  setSelectedYear(Number(event.target.value))
                }
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
        <div className="card-body">
          {analyticsLoading ? (
            <div className="family-clans-state">
              Loading contribution chart…
            </div>
          ) : analyticsError ? (
            <div className="family-clans-state text-danger">
              {analyticsError}
            </div>
          ) : (
            <div className="family-clan-chart-shell">
              <div className="family-clan-chart-canvas">
                <Bar data={chartData} options={chartOptions} />
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="family-clan-detail-grid">
        <section
          className="card family-clan-about"
          aria-labelledby="about-clan"
        >
          <div className="card-header">
            <h2 id="about-clan">Clan details</h2>
          </div>
          <div className="card-body">
            {resetFeedback && (
              <div
                className={`family-clan-reset-feedback family-clan-reset-feedback-${resetFeedback.type}`}
              >
                {resetFeedback.message}
              </div>
            )}
            <dl>
              <ResettableDetailRow
                label="Name"
                field="name"
                onReset={handleResetField}
                resettingField={resettingField}
              >
                {clan.name}
              </ResettableDetailRow>
              <ResettableDetailRow
                label="Catch phrase"
                field="catchPhrase"
                onReset={handleResetField}
                resettingField={resettingField}
              >
                {clan.catchPhrase}
              </ResettableDetailRow>
              <ResettableDetailRow
                label="Tag name"
                field="tagName"
                onReset={handleResetField}
                resettingField={resettingField}
              >
                {clan.tagName}
              </ResettableDetailRow>
              <ResettableDetailRow
                label="Rules"
                field="rules"
                onReset={handleResetField}
                resettingField={resettingField}
              >
                <span className="family-clan-rules">{clan.rules}</span>
              </ResettableDetailRow>
              <DetailRow label="Clan ID">
                <code>{clan.clanId}</code>
              </DetailRow>
              <DetailRow label="Level">
                <span className="family-clan-level">
                  <img src={clan.level?.image || noImage} alt="" />
                  {clan.level?.name || "Unranked"}
                </span>
              </DetailRow>
              <DetailRow label="Created">
                {formatDate(clan.createdAt)}
              </DetailRow>
            </dl>
          </div>
        </section>

        <section
          className="card family-clan-roster"
          aria-labelledby="clan-members"
        >
          <div className="card-header family-clan-roster-heading">
            <div>
              <h2 id="clan-members">Members</h2>
              {appliedDateRange && (
                <p className="family-clan-roster-period mb-0">
                  Contributions from {formatDate(appliedDateRange.startDate)} to{" "}
                  {formatDate(appliedDateRange.endDate)}
                </p>
              )}
            </div>
            <div className="family-clan-roster-actions">
              <span>{rosterMembers.length || clan.members?.length || 0} total</span>
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={() => setShowDateRange((visible) => !visible)}
                aria-expanded={showDateRange}
              >
                {showDateRange ? "Hide date range" : "Contribution by date range"}
              </button>
            </div>
          </div>
          {showDateRange && (
            <form
              className="family-clan-roster-filters"
              onSubmit={handleContributionRangeSubmit}
            >
              <label>
                <span>Start date</span>
                <input
                  type="date"
                  className="form-control"
                  value={contributionStartDate}
                  onChange={(event) => setContributionStartDate(event.target.value)}
                  max={contributionEndDate || undefined}
                />
              </label>
              <label>
                <span>End date</span>
                <input
                  type="date"
                  className="form-control"
                  value={contributionEndDate}
                  onChange={(event) => setContributionEndDate(event.target.value)}
                  min={contributionStartDate || undefined}
                />
              </label>
              <div className="family-clan-roster-filter-actions">
                <button type="submit" className="btn btn-sm btn-primary">
                  Show contributions
                </button>
                {appliedDateRange && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    onClick={handleClearContributionRange}
                  >
                    Show all time
                  </button>
                )}
              </div>
            </form>
          )}
          <div className="family-clan-member-list">
            {rosterLoading ? (
              <div className="family-clans-state">Loading members…</div>
            ) : rosterError ? (
              <div className="family-clans-state text-danger">{rosterError}</div>
            ) : (
              rosterMembers.map((member, index) => (
              <div className="family-clan-member" key={member._id || index}>
                <span className="family-clan-member-rank">{index + 1}</span>
                <img
                  src={member.image || noImage}
                  onError={(event) => {
                    event.currentTarget.src = noImage;
                  }}
                  alt=""
                />
                <div className="family-clan-member-name">
                  <strong>
                    {member.name || member.username || "Unknown member"}
                  </strong>
                  <span>
                    ID {member.uniqueId || "—"} · {member.role}
                  </span>
                </div>
                <div className="family-clan-member-level">
                  {member.level?.image && (
                    <img src={member.level.image} alt="" />
                  )}
                  <span>{member.level?.name || "No level"}</span>
                </div>
                <div className="family-clan-contribution">
                  <strong>
                    {numberFormat.format(member.contribution || 0)}
                  </strong>
                  <span>Total Contribution</span>
                </div>
              </div>
              ))
            )}
            {!rosterLoading && !rosterError && !rosterMembers.length && (
              <div className="family-clans-state">No members to show.</div>
            )}
          </div>
        </section>
      </div>
    </article>
  );
};

export default connect(null, {
  getFamilyClan,
  getFamilyClanAnalytics,
  clearFamilyClan,
})(FamilyClanDetail);
